import { geoAlbers, geoConicEqualArea, GeoConicProjection } from 'd3-geo';

import { AircraftPositionLeg, TPoint } from '#entities';
import { MAP_OVERLAY_VIEW_OFFSET_LEFT, MAP_OVERLAY_VIEW_WIDTH } from '#shared';

import { getPartOfMap } from './getPartOfMap';

import type { Point } from '#shared';

const xmlns = 'http://www.w3.org/2000/svg';
const DEFAULT_OFFSET = {
  top: 130,
  left: 252,
};

const OFFSETS: Record<string, Record<string, number>> = {
  alaska: {
    top: -95,
    left: -155,
  },
  lower48: DEFAULT_OFFSET,
  canada: DEFAULT_OFFSET,
  hawaii: {
    top: 315,
    left: -337,
  },
  puertoRico: {
    top: 427,
    left: 698,
  },
};

const DEFAULT_PROJECTION = geoAlbers().scale(1330);

const ALBERS_PROJECTIONS: Record<string, GeoConicProjection> = {
  alaska: geoConicEqualArea()
    .scale(485)
    .rotate([116, 0.5, -0.4])
    .center([-2, 58.5])
    .parallels([55, 65]),
  lower48: DEFAULT_PROJECTION,
  canada: DEFAULT_PROJECTION,
  hawaii: geoConicEqualArea()
    .scale(1700)
    .rotate([157, 0])
    .center([-3, 19.9])
    .parallels([8, 18]),
  puertoRico: geoConicEqualArea()
    .scale(2400)
    .rotate([66, 0])
    .center([0, 18])
    .parallels([8, 18]),
};

export const getPointPixelsCoordinates = (
  [a, b]: TPoint,
  boundary: string,
  isWeather = false,
): TPoint => {
  if (isWeather && boundary === 'canada') {
    return [-1000, -1000];
  }

  const albersProjection = ALBERS_PROJECTIONS[boundary];
  const offsets = OFFSETS[boundary];

  const projected = albersProjection?.([a, b]);

  if (projected) {
    // eslint-disable-next-line prefer-const
    let [x, y] = projected;
    // set item closer to east coast of USA
    if (x > 980) {
      x = 980;
    }

    const yPosition = y + offsets.top;
    const xPosition = x + offsets.left;

    if (yPosition < 55 && boundary === 'canada') {
      return [xPosition, 55];
    }

    return [xPosition, yPosition];
  }

  return [-1000, -1000];
};

export const getPoint = (point: TPoint) => {
  const boundary = getPartOfMap(point);

  if (boundary) {
    return getPointPixelsCoordinates(point, boundary, true);
  }
};

export const getPolygon = (points: TPoint[]) =>
  points
    .reduce<TPoint[]>((acc, point) => {
      const boundary = getPartOfMap(point);

      if (boundary) {
        acc.push(getPointPixelsCoordinates(point, boundary));
      }
      return acc;
    }, [])
    .map(([x, y]) => `${x},${y}`)
    .join(' ');

// Per-epoch lane registry: unique lanes per (A,B) within an epoch
let curEpoch = '';
const laneRegistry = new Map<string, Map<string, number>>();
// Reverse index: caseId → current pairKey, so stale pixel entries are evicted on pan
const linkPairIndex = new Map<string, string>();

// Order-invariant key for (A,B)
const pairKeyPx = (a: [number, number], b: [number, number]) => {
  const s1 = `${a[0].toFixed(2)},${a[1].toFixed(2)}`;
  const s2 = `${b[0].toFixed(2)},${b[1].toFixed(2)}`;
  return [s1, s2].sort().join('|');
};

const laneBalanced = (i: number) => -(1 - (i % 2) * 2) * Math.ceil(i / 2);

const getUniqueLane = (pairKey: string, linkId: string) => {
  // Evict stale entry when pixel coords changed (map pan) for the same linkId
  const prevPairKey = linkPairIndex.get(linkId);
  if (prevPairKey && prevPairKey !== pairKey) {
    const prevLane = laneRegistry.get(prevPairKey);
    if (prevLane) {
      prevLane.delete(linkId);
      if (prevLane.size === 0) {
        laneRegistry.delete(prevPairKey);
      }
    }
  }
  linkPairIndex.set(linkId, pairKey);

  let lane = laneRegistry.get(pairKey);
  if (!lane) {
    lane = new Map();
    laneRegistry.set(pairKey, lane);
  }

  const lineId = lane.get(linkId);
  if (lineId !== undefined) {
    return laneBalanced(lineId);
  }

  const maxId = Math.max(0, ...lane.values());
  const newId = lane.size ? maxId + 1 : 1;
  lane.set(linkId, newId);
  return laneBalanced(newId);
};

/**
 * Pass seed as "<epoch>|<linkId>" to guarantee unique lanes per (A,B) in that epoch.
 * Any other seed falls back to hash-based lane pick.
 */
export const getCurve = (
  [x1, y1]: [number, number],
  [x2, y2]: [number, number],
  seed: string = '',
): string => {
  const mpx = (x1 + x2) * 0.5;
  const mpy = (y1 + y2) * 0.5;
  const theta = Math.atan2(y2 - y1, x2 - x1) - Math.PI / 2;

  const dist = Math.hypot(x2 - x1, y2 - y1);

  let laneIdx = 1;

  // Unique lanes per (A,B) per epoch when seed is "epoch|id"
  if (seed.includes('|')) {
    const [epoch, linkId] = seed.split('|');
    if (epoch !== curEpoch) {
      curEpoch = epoch;
      laneRegistry.clear();
      linkPairIndex.clear();
    }
    laneIdx = getUniqueLane(pairKeyPx([x1, y1], [x2, y2]), linkId);
  }
  // flip lane side for reverse direction so A→B and B→A don't overlap
  const sA = `${x1.toFixed(2)},${y1.toFixed(2)}`;
  const sB = `${x2.toFixed(2)},${y2.toFixed(2)}`;
  const dirSign = sA <= sB ? +1 : -1;
  const offset = (dist / 8) * laneIdx * dirSign;

  const c1x = mpx + offset * Math.cos(theta);
  const c1y = mpy + offset * Math.sin(theta);
  return `M${x1},${y1} Q${c1x},${c1y} ${x2},${y2}`;
};

export const getNewCaseLine = (
  [x1, y1]: number[],
  cardYPosition: number,
  offset: number,
  isWest = true,
): string => {
  const cardXPosition =
    (isWest ? offset : MAP_OVERLAY_VIEW_WIDTH) - MAP_OVERLAY_VIEW_OFFSET_LEFT;

  return `M${x1},${y1} ${cardXPosition},${cardYPosition}`;
};

export const getLine = ([x1, y1]: number[], [x2, y2]: number[]): string =>
  `M${x1},${y1} ${x2},${y2}`;

type GetAircraftPositionParams = {
  projection: d3.GeoProjection;
  coordinates: Point;
  leg?: AircraftPositionLeg;
};

export const getAircraftPosition = ({
  projection,
  coordinates,
  leg,
}: GetAircraftPositionParams): number[] | null => {
  const [pointX, pointY] = projection(coordinates) as [number, number];

  if (pointX !== null && pointY !== null && !isNaN(pointX) && !isNaN(pointY)) {
    return [pointX, pointY];
  }

  if (
    leg?.start.Longitude &&
    leg?.start.Latitude &&
    leg?.end.Longitude &&
    leg?.end.Latitude
  ) {
    const startCoordinates = projection([
      leg.start.Longitude,
      leg.start.Latitude,
    ]) as Point;
    const endCoordinates = projection([
      leg.end.Longitude,
      leg.end.Latitude,
    ]) as Point;
    const line = getLine(startCoordinates, endCoordinates);

    const path = document.createElementNS(xmlns, 'path');
    path.setAttributeNS(null, 'd', line);
    const pathLength = path.getTotalLength();

    const dateNowMs = Date.now();
    const dateStartMs = new Date(leg.startTime).getTime();
    const dateEndMs = new Date(leg.endTime).getTime();
    const progress =
      (100 / (dateEndMs - dateStartMs)) * (dateNowMs - dateStartMs);

    const { x, y } = path.getPointAtLength(pathLength * (progress / 100));

    return [x, y];
  }

  return null;
};
